import { useNavigate } from 'react-router-dom';
import { Login, Spinner } from '@monkvision/common-ui-web';
import { useAsyncEffect, useLoadingState, useMonkAppState } from '@monkvision/common';
import { useEffect, useMemo } from 'react';
import { isTokenExpired } from '@monkvision/network';
import { useTranslation } from 'react-i18next';
import { Page } from '../pages';
import { useGetCaptureToken } from '../../hooks';
import styles from './LoginPage.module.css';

export function LoginPage() {
  const { i18n, t } = useTranslation();
  const { authToken, setAuthToken, config, inspectionId } = useMonkAppState();
  const loading = useLoadingState();
  const navigate = useNavigate();
  const { getCaptureToken } = useGetCaptureToken();

  const isExpired = useMemo(() => authToken && isTokenExpired(authToken), [authToken]);

  useAsyncEffect(
    () => {
      if (authToken || !inspectionId) {
        return Promise.resolve(null);
      }
      loading.start();
      return getCaptureToken(inspectionId);
    },
    [inspectionId, getCaptureToken, authToken, setAuthToken],
    {
      onResolve: (token) => {
        loading.onSuccess();
        if (token) {
          setAuthToken(token);
        }
      },
      onReject: (_err) => {
        loading.onError('login.errors.unknown');
      },
    },
  );

  useEffect(() => {
    if (!inspectionId && !config.allowManualLogin) {
      loading.onError('login.errors.missing-param');
    }
    if (authToken && !isExpired) {
      navigate(Page.CREATE_INSPECTION);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken, isExpired, config, inspectionId]);

  return (
    <div className={styles['container']}>
      {loading.isLoading && <Spinner size={80} />}
      {!loading.isLoading && isExpired && (
        <div className={styles['errorMessage']}>{t('login.errors.token-expired')}</div>
      )}
      {!loading.isLoading && loading.error && (
        <div className={styles['errorMessage']}>{t(loading.error as string)}</div>
      )}
      {!loading.isLoading && !loading.error && !authToken && config.allowManualLogin && (
        <Login lang={i18n.language} onLoginSuccessful={() => navigate(Page.CREATE_INSPECTION)} />
      )}
    </div>
  );
}
